import { Button, Dialog, Text } from '@acast/decibel';
import { hook } from 'acast-www-utils';
import T from 'prop-types';
import React, { Component } from 'react';
import { dismissModal, presentModal } from 'services/modals';

const MODAL_ACTIONS = Object.freeze({
  OK: [{ name: 'Ok', value: true }],
  OK_CANCEL: [
    { name: 'Cancel', value: false, variant: 'tertiary' },
    { name: 'Ok', value: true, variant: 'primary' },
  ],
  DONE: [{ name: 'Done', value: true }],
  SAVE_DISMISS: [
    { name: 'Dismiss', value: false, variant: 'tertiary' },
    { name: 'Save', value: true, variant: 'primary' },
  ],
});

const DEFAULT_ACTIONS = MODAL_ACTIONS.OK_CANCEL;

function createBoundModalInvoker(instance, modal, callback) {
  const bound = (...args) => {
    const original = instance.componentWillUnmount;
    hook(instance, 'componentWillUnmount', () => {
      // eslint-disable-next-line no-use-before-define
      Modal.dismiss(instance);
    });
    // eslint-disable-next-line no-use-before-define
    Modal.present({
      ...modal,
      scope: instance,
      onDismiss: result => {
        // eslint-disable-next-line no-param-reassign
        instance.componentWillUnmount = original;
        callback(result, ...args);
      },
    });
  };

  return bound;
}

// eslint-disable-next-line no-use-before-define
function createBindModalDecorator(props) {
  return (target, key, descriptor) => {
    const fn = descriptor.value;
    if (typeof fn !== 'function') {
      throw new Error('Modals can only be bound to functions');
    }
    return {
      configurable: true,
      get() {
        // eslint-disable-next-line
        if (this === target.prototype || this.hasOwnProperty(key)) {
          return fn;
        }
        const bound = createBoundModalInvoker(this, props, fn);
        Object.defineProperty(this, key, {
          value: bound,
          configurable: true,
          writable: true,
        });
        return bound;
      },
    };
  };
}

export default class Modal extends Component {
  static present(props) {
    // since the presentModal action is referencing the Modal
    // atom we have to run it through the namespace and cannot
    // import it directly.
    return presentModal(<Modal {...props} />);
  }

  static dismiss(scope) {
    if (scope) {
      dismissModal(scope);
    }
  }

  static bind(...args) {
    // Legacy! Keep it here for now though until we
    // decide to fully remove decorators.
    if (args.length === 1) {
      const props = args[0];
      if (Object(props) === props) {
        return createBindModalDecorator(props);
      }

      throw new Error(
        'When providing a single configuration object you need to use bind to decorate a function via @Modal.bind'
      );
    } else if (args.length === 2) {
      const element = args[0];
      const props = args[1];

      if (element && element.isReactComponent && Object(props) === props) {
        return new Promise((resolve, reject) => {
          try {
            const original = props.onDismiss;
            const invoker = createBoundModalInvoker(element, props, value => {
              if (typeof original === 'function') {
                original(value);
              }
              resolve(value);
            });
            invoker();
          } catch (e) {
            reject(e);
          }
        });
      }

      throw new Error(
        'When invoking Model.bind from within a class function you should supply the instance as the first argument and the modal properties as the second'
      );
    }

    throw new Error(
      'Incorrect usage. Either specify as a decorator @Modal.bind({}) or use directly and pass in instance as Modal.bind(this, {})'
    );
  }

  static Actions = MODAL_ACTIONS;

  static DEFAULT_ACTIONS = DEFAULT_ACTIONS;

  static propTypes = {
    scope: T.object,
    title: T.string,
    dialog: T.oneOfType([T.string, T.node]),
    actions: T.array,
    onDismiss: T.func,
    className: T.string,
  };

  static defaultProps = {
    scope: null,
    title: null,
    dialog: null,
    actions: DEFAULT_ACTIONS,
    onDismiss: null,
    className: '',
  };

  getTitle() {
    const { title } = this.props;

    if (title) {
      return (
        <Dialog.Header>
          <Dialog.Title data-test-id="Modal__title">{title}</Dialog.Title>
        </Dialog.Header>
      );
    }
    return null;
  }

  getDialog() {
    const { dialog, onDismiss } = this.props;
    const isSimpleDialog = typeof dialog === 'string';

    let wrappedOnDismiss;
    if (!isSimpleDialog && typeof dialog.props.onDismiss === 'function') {
      wrappedOnDismiss = value => {
        dialog.props.onDismiss(value);
        onDismiss(value);
      };
    }

    const content = isSimpleDialog ? <Text mb={0}>{dialog}</Text> : dialog;

    if (dialog) {
      return (
        <Dialog.Body data-test-id="Modal__dialog">
          {!wrappedOnDismiss
            ? content
            : React.cloneElement(content, { onDismiss: wrappedOnDismiss })}
        </Dialog.Body>
      );
    }
    return null;
  }

  getActions() {
    const { actions, dialog, onDismiss } = this.props;

    if (actions.length) {
      return (
        <Dialog.Footer data-test-id="Modal__actions">
          {actions.map(action => {
            const key = `modal-action--${action.name.toLowerCase()}`;
            const { intent, variant = 'primary', glyph, ...rest } = action;

            const onClick = () => {
              if (typeof this.props.onDismiss === 'function') {
                if (React.isValidElement(dialog) && typeof dialog.props.onDismiss === 'function') {
                  dialog.props.onDismiss(action.value);
                }
                onDismiss(action.value);
              }
            };

            return (
              <Button
                key={key}
                variant={variant}
                intent={intent}
                glyph={glyph}
                onClick={onClick}
                {...rest}
              >
                {action.name}
              </Button>
            );
          })}
        </Dialog.Footer>
      );
    }
    return null;
  }

  render() {
    const { variant, onDismiss } = this.props;
    return (
      <Dialog isOpen variant={variant} onDismiss={onDismiss}>
        {this.getTitle()}
        {this.getDialog()}
        {this.getActions()}
      </Dialog>
    );
  }
}
